import { forwardRef } from 'react'
import {Sparkles} from 'lucide-react'

interface ScoreCardProps {
  name: string
  company: string
  tierTitle: string
  tierLevel: 'emerging' | 'developing' | 'established' | 'leading'
  percent: number
  totalScore: number
  maxScore: number
  categories: { category: string; percent: number }[]
}

const tierAccent: Record<string, { from: string; to: string; ink: string }> = {
  emerging: { from: '#F59E0B', to: '#FBBF24', ink: '#0A0A0A' },
  developing: { from: '#0F766E', to: '#14B8A6', ink: '#FFFFFF' },
  established: { from: '#0F766E', to: '#0B524C', ink: '#FFFFFF' },
  leading: { from: '#0A0A0A', to: '#1F2937', ink: '#FFFFFF' },
}

const ScoreCard = forwardRef<HTMLDivElement, ScoreCardProps>(function ScoreCard(
  { name, company, tierTitle, tierLevel, percent, totalScore, maxScore, categories },
  ref
) {
  const accent = tierAccent[tierLevel]
  const topCategories = [...categories].sort((a, b) => b.percent - a.percent).slice(0, 3)

  return (
    <div
      ref={ref}
      className="relative w-[1200px] h-[630px] flex flex-col"
      style={{
        background: '#FAFAF7',
        fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
      }}
    >
      {/* Decorative gradient blob */}
      <div
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-30"
        style={{
          background: `radial-gradient(circle, ${accent.from}, transparent 70%)`,
        }}
      />
      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(#0A0A0A 1px, transparent 1px), linear-gradient(90deg, #0A0A0A 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-16 pt-14">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: '#0A0A0A' }}
          >
            <Sparkles size={20} style={{ color: '#F59E0B' }} />
          </div>
          <div>
            <div
              className="text-[11px] uppercase tracking-[0.2em]"
              style={{ color: '#0A0A0A', opacity: 0.5, fontFamily: 'ui-monospace, monospace' }}
            >
              Triangle Project Consultants
            </div>
            <div className="text-base font-semibold" style={{ color: '#0A0A0A' }}>
              AI Readiness Assessment
            </div>
          </div>
        </div>
        <div
          className="text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-full"
          style={{
            background: '#0A0A0A',
            color: '#F59E0B',
            fontFamily: 'ui-monospace, monospace',
          }}
        >
          Verified Result
        </div>
      </div>

      {/* Body */}
      <div className="relative z-10 flex-1 grid grid-cols-12 gap-10 px-16 pt-10 pb-12">
        {/* Left — score */}
        <div className="col-span-7 flex flex-col justify-center">
          <div
            className="text-[12px] uppercase tracking-[0.25em] mb-4"
            style={{ color: accent.from, fontFamily: 'ui-monospace, monospace' }}
          >
            {name} · {company}
          </div>
          <div
            className="font-serif leading-[0.95] mb-6"
            style={{
              color: '#0A0A0A',
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: '76px',
              fontWeight: 500,
              letterSpacing: '-0.02em',
            }}
          >
            My AI readiness:
            <br />
            <em
              style={{
                color: accent.from,
                fontStyle: 'italic',
                fontWeight: 600,
              }}
            >
              "{tierTitle}"
            </em>
          </div>
          <div className="flex items-end gap-6">
            <div
              className="rounded-3xl px-8 py-5"
              style={{
                background: `linear-gradient(135deg, ${accent.from}, ${accent.to})`,
                color: accent.ink,
              }}
            >
              <div
                className="text-[10px] uppercase tracking-[0.25em] mb-1 opacity-80"
                style={{ fontFamily: 'ui-monospace, monospace' }}
              >
                Score
              </div>
              <div
                className="font-serif tabular-nums leading-none"
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontSize: '64px',
                  fontWeight: 600,
                }}
              >
                {percent}
                <span style={{ fontSize: '32px', opacity: 0.6 }}>%</span>
              </div>
            </div>
            <div>
              <div
                className="text-[10px] uppercase tracking-[0.25em] mb-1"
                style={{ color: '#0A0A0A', opacity: 0.4, fontFamily: 'ui-monospace, monospace' }}
              >
                Raw points
              </div>
              <div
                className="tabular-nums font-semibold"
                style={{ color: '#0A0A0A', fontSize: '28px' }}
              >
                {totalScore}
                <span style={{ color: '#0A0A0A', opacity: 0.3 }}>/{maxScore}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right — categories */}
        <div className="col-span-5 flex flex-col justify-center">
          <div
            className="rounded-3xl p-7"
            style={{
              background: '#0A0A0A',
              color: '#FAFAF7',
            }}
          >
            <div
              className="text-[10px] uppercase tracking-[0.25em] mb-5"
              style={{ color: '#F59E0B', fontFamily: 'ui-monospace, monospace' }}
            >
              Top Strengths
            </div>
            <div className="space-y-4">
              {topCategories.map((cat) => (
                <div key={cat.category}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>{cat.category}</span>
                    <span
                      className="tabular-nums"
                      style={{ fontSize: '13px', color: '#F59E0B', fontFamily: 'ui-monospace, monospace' }}
                    >
                      {cat.percent}%
                    </span>
                  </div>
                  <div
                    className="h-1.5 rounded-full overflow-hidden"
                    style={{ background: 'rgba(255,255,255,0.08)' }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${cat.percent}%`,
                        background: `linear-gradient(90deg, ${accent.from}, ${accent.to})`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="relative z-10 flex items-center justify-between px-16 pb-10"
        style={{
          color: '#0A0A0A',
          opacity: 0.6,
        }}
      >
        <div
          className="text-[11px] uppercase tracking-[0.25em]"
          style={{ fontFamily: 'ui-monospace, monospace' }}
        >
          Take the assessment · triangleprojectconsultants.com
        </div>
        <div
          className="text-[11px] uppercase tracking-[0.25em]"
          style={{ fontFamily: 'ui-monospace, monospace' }}
        >
          Made in Raleigh, NC · USA
        </div>
      </div>
    </div>
  )
})

export default ScoreCard
